import { useState, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import jwt_decode from "jwt-decode";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import logo from "../../icons/shiprocket.png";
import { config } from "../../utils/config";
import "./Login.css";
import { useEffect } from "react";

const { apiEndpoint } = config;
const OTP_LEN = 6;

const Login = () => {
    const history = useHistory();
    const location = useLocation();

    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState<string[]>(Array(OTP_LEN).fill(""));
    const [showOtp, setShowOtp] = useState(false);
    const [loading, setLoading] = useState(false);
    const [autoLoggingIn, setAutoLoggingIn] = useState(false);
    const [timer, setTimer] = useState(30);
    const [resendCount, setResendCount] = useState(0);
    const [otpCount, setOtpCount] = useState<number | null>(null);
    const [isLimitExceeded, setIsLimitExceeded] = useState<boolean>(false);
    const [limitMessage, setLimitMessage] = useState<string>("");

    const otpRefs = useRef<Array<HTMLInputElement | null>>([]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const navigateToSeller = params.get("navigateToSeller");
        const tokenFromUrl = params.get("token");
        const providerId = params.get("provider_id");
        const contactNo = params.get("contact_no");
        const sellerId = params.get("seller_id");

        console.log("sdfghjdfg", navigateToSeller, tokenFromUrl, providerId, contactNo, sellerId);

        if (navigateToSeller === "true" && tokenFromUrl && providerId && contactNo && sellerId) {
            loginWithProvider(tokenFromUrl, providerId, contactNo, sellerId);
        }
    }, []);

    useEffect(() => {
        let interval: any = null;
        if (showOtp && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [showOtp, timer]);

    // ── Auto-login via provider (new flow) ─────────────────────────────────
    const loginWithProvider = async (
        tokenFromUrl: string,
        providerId: string,
        contactNo: string,
        sellerId: string
    ) => {
        try {
            setAutoLoggingIn(true);
            const { data }: any = await axios.post(
                `${apiEndpoint}/snp/login`,
                {
                    provider_id: providerId,
                    contact_no: contactNo,
                    seller_id: sellerId,
                },
                {
                    headers: {
                        Authorization: `Bearer ${tokenFromUrl}`,
                    },
                }
            );

            if (data?.token) {
                sessionStorage.setItem("token", data.token);
                sessionStorage.setItem("mobile", contactNo);
                toast.success("Login Successful");
                history.push("/dashboard");
            } else {
                toast.error(data?.message || "Auto login failed. Please login with OTP.");
            }
        } catch {
            toast.error("Auto login failed. Please login with OTP.");
        } finally {
            setAutoLoggingIn(false);
        }
    };


    const formatTimer = (totalSeconds: number) => {
        if (totalSeconds >= 60) {
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return `${minutes}:${seconds < 10 ? "0" : ""}${seconds} min`;
        }
        return `${totalSeconds}s`;
    };

    // const onMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    //     setMobile(val);
    //     if (val.length !== 10) {
    //         setResendCount(0);
    //         setOtpCount(null);
    //         setIsLimitExceeded(false);
    //         setLimitMessage("");
    //     }
    // };

    const onMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
        setMobile(val);

        if (val.length === 0) {
            setMobileError("");
        } else if (val.length < 10) {
            setMobileError("Please enter a valid 10-digit mobile number.");
        } else if (!/^[6-9]\d{9}$/.test(val)) {
            setMobileError("Please enter a valid mobile number.");
        } else {
            setMobileError("");
        }

        if (val.length !== 10) {
            setResendCount(0);
            setOtpCount(null);
            setIsLimitExceeded(false);
            setLimitMessage("");
        }
    };

    const onOtpInput = (idx: number, value: string) => {
        if (!/^\d?$/.test(value)) return;
        const next = [...otp];
        next[idx] = value;
        setOtp(next);
        if (value && idx < OTP_LEN - 1) otpRefs.current[idx + 1]?.focus();
    };

    const onOtpKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !otp[idx] && idx > 0) {
            const prev = [...otp];
            prev[idx - 1] = "";
            setOtp(prev);
            otpRefs.current[idx - 1]?.focus();
        }
    };

    const onOtpPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LEN);
        const next = Array(OTP_LEN).fill("");
        digits.split("").forEach((d, i) => { next[i] = d; });
        setOtp(next);
        otpRefs.current[Math.min(digits.length, OTP_LEN - 1)]?.focus();
    };

    const [mobileError, setMobileError] = useState("");
    // ── Send OTP ──────────────────────────────────────────────────────────
    const sendOtp = async () => {
        if (mobile.length !== 10 || !/^[6-9]\d{9}$/.test(mobile)) {
            setMobileError("Please enter a valid 10-digit mobile number.");
            return;
        }

        if (isLimitExceeded) {
            toast.error(limitMessage || "OTP limit exceeded. You can request maximum 3 OTPs within 1 hour.");
            return;
        }

        if (showOtp && timer > 0) {
            toast.info(`Please wait ${formatTimer(timer)} before resending OTP.`);
            return;
        }

        try {
            setLoading(true);
            setMobileError("");
            const { data }: any = await axios.post(`${apiEndpoint}/snp/otp/send`, {
                contact_no: mobile,
            });

            if (data.status === true) {
                toast.success("OTP sent successfully.");
                setOtp(Array(OTP_LEN).fill(""));
                setShowOtp(true);

                const currentOtpCount = typeof data.otp_count === "number" ? data.otp_count : (resendCount + 1);
                setOtpCount(currentOtpCount);

                let nextDuration = 30;
                if (!showOtp || currentOtpCount === 1) {
                    nextDuration = 30; // 1st OTP -> 30s
                } else if (currentOtpCount === 2) {
                    nextDuration = 120; // 2nd OTP -> 120s (2:00 min)
                } else if (currentOtpCount === 3) {
                    nextDuration = 300; // 3rd OTP -> 300s (5:00 min)
                } else {
                    nextDuration = 1800; // 4th OTP+ -> 1800s (30:00 min)
                }

                setResendCount(currentOtpCount);
                setTimer(nextDuration);
                setTimeout(() => otpRefs.current[0]?.focus(), 100);
            } else {
                // If backend returned status: false (limit exceeded)
                setIsLimitExceeded(true);
                setTimer(0);
                const msg = data?.message || "OTP limit exceeded. You can request maximum 3 OTPs within 1 hour.";
                setLimitMessage(msg);
                toast.error(msg);
            }
        } catch (error: any) {
            const errRes = error?.response?.data;
            const statusCode = error?.response?.status;
            if (statusCode === 429 || (errRes && errRes.status === false)) {
                setIsLimitExceeded(true);
                setTimer(0);
                const msg = errRes?.message || "OTP limit exceeded. You can request maximum 3 OTPs within 1 hour.";
                setLimitMessage(msg);
                toast.error(msg);
            } else {
                toast.error(errRes?.message || "Something went wrong.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Verify OTP ────────────────────────────────────────────────────────
    const verifyOtp = async (customOtp?: string) => {
        const otpVal = typeof customOtp === "string" ? customOtp : otp.join("");
        if (otpVal.length !== OTP_LEN) {
            toast.error("Please enter the complete 6-digit OTP.");
            return;
        }
        if (loading) return;
        try {
            setLoading(true);
            const { data }: any = await axios.post(`${apiEndpoint}/snp/otp/verify`, {
                contact_no: mobile,
                otp_code: Number(otpVal),
            });

            if (data.status) {
                sessionStorage.setItem("token", data.token);
                sessionStorage.setItem("mobile", mobile);

                console.log("Login Response:", data);

                toast.success("Login Successful");

                sessionStorage.removeItem("isSkipped");

                // Check seller status from decoded token 
                let isFullyApproved = false;
                let isKycCompleted = false;
                console.log("isKycCompleted22222222222", isKycCompleted);

                try {
                    const decoded: any = jwt_decode(data.token);
                    const userObj = Array.isArray(decoded?.data?.user) ? decoded?.data?.user[0] : (decoded?.data?.user || {});
                    const sellerObj = Array.isArray(decoded?.data?.seller_data) ? decoded?.data?.seller_data[0] : (decoded?.data?.seller_data || {});
                    const approveStatus = userObj?.approve_status !== undefined && userObj?.approve_status !== null
                        ? userObj?.approve_status
                        : sellerObj?.approve_status;
                    const sellerId = userObj?.seller_id || sellerObj?.id;
                    const isRootAdmin = String(userObj?.parent_id) === "0" || Number(sellerId) === 1 || Number(userObj?.id) === 1;

                    const isSubUser = Number(userObj?.parent_id) > 1;

                    isKycCompleted = Boolean(
                        userObj?.kyc_status === true ||
                        userObj?.kyc_status === "true" ||
                        userObj?.kyc_status === 1 ||
                        sellerObj?.kyc_status === true ||
                        sellerObj?.kyc_status === "true" ||
                        sellerObj?.kyc_status === 1


                    );

                    const isKycNull = userObj?.kyc_status === null || userObj?.kyc_status === undefined;

                    if (isRootAdmin || isSubUser || (approveStatus === 1 || approveStatus === "1")) {
                        isFullyApproved = true;
                    }
                    if (isSubUser || isKycNull) {
                        isKycCompleted = true;
                    }

                    if (isSubUser) {
                        sessionStorage.setItem("isSkipped", "true");
                        sessionStorage.setItem("is_activated", "true");
                        sessionStorage.setItem("seller_approved", "1");
                    }


                } catch (e) {
                    console.error("Token decode error on login:", e);
                }


                if (isFullyApproved) {
                    history.push("/dashboard");
                } else if (isKycCompleted) {
                    // KYC is already completed, but pending admin approval -> will show Inactive screen
                    history.push("/dashboard");
                } else {
                    // KYC is pending (kyc_status is null / false) -> go to KYC onboard
                    history.push("/Sellerdetails_kyc");
                }
            } else {
                toast.error(data.message || "Invalid OTP.");
            }
        } catch {
            toast.error("OTP verification failed.");
        } finally {
            setLoading(false);
        }
    };

    // Auto verify OTP as soon as all 6 digits are entered
    useEffect(() => {
        const fullOtp = otp.join("");
        if (showOtp && fullOtp.length === OTP_LEN && otp.every(d => d !== "") && !loading) {
            verifyOtp(fullOtp);
        }
    }, [otp, showOtp]);

    const otpComplete = otp.every(d => d !== "");

    if (autoLoggingIn) {
        return (
            <section
                className="auto-login-container"
                style={{
                    backgroundImage: "url('img/backimg.png')",
                }}
            >
                <div className="auto-login-overlay" />
                <div className="auto-login-card">
                    <div className="auto-login-logo-wrapper">
                        <div className="auto-login-ripple" />
                        <div className="auto-login-spinner-ring" />
                        <div className="auto-login-logo-circle">
                            <img className="auto-login-logo-img" src={logo} alt="Logo" />
                        </div>
                    </div>

                    <h2 className="auto-login-title">
                        Logging you in
                        <span className="auto-login-dots">
                            <span>.</span><span>.</span><span>.</span>
                        </span>
                    </h2>

                    <p className="auto-login-subtitle">
                        Please wait while we set up your session and take you to your dashboard.
                    </p>

                    <div className="auto-login-progress-track">
                        <div className="auto-login-progress-bar" />
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            <section
                className="login-bg-section"
                style={{
                    backgroundImage: "url('img/backimg.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    minHeight: "100vh",
                    height: "100vh",
                }}
            >
                <div className="login-card-panel">
                    <img className="login-card-panel__logo" src={logo} alt="logo" />

                    {!showOtp && (
                        <>
                            <h1 className="login-card__title">Welcome Back!</h1>
                            <p className="login-card__subtitle">
                                Login to continue your journey towards a{" "}
                                <span>smarter &amp; more profitable business</span>
                            </p>
                        </>
                    )}

                    {/* ── Mobile number input ── */}
                    {!showOtp && (
                        <>
                            <label className="login-card__label">Enter Mobile Number</label>
                            <div className="login-input">
                                <span className="login-input__prefix">
                                    <span className="flag">🇮🇳</span> +91
                                </span>
                                <input
                                    type="text"
                                    placeholder="Enter your mobile number"
                                    name="mobile"
                                    value={mobile}
                                    maxLength={10}
                                    inputMode="numeric"
                                    onChange={onMobileChange}
                                />
                            </div>
                            <div style={{ minHeight: "18px", marginTop: "4px" }}>
                                {mobileError && (
                                    <span className="text-danger small" style={{ fontSize: "14px" }}>
                                        {mobileError}
                                    </span>
                                )}
                            </div>
                        </>
                    )}

                    {/* Change number link */}
                    {showOtp && (
                        <div className="mt-1 mb-2">
                            <p className="login-card__label mb-1">
                                Enter 6-digit OTP sent to +91 {mobile}
                            </p>
                            <button
                                onClick={() => {
                                    setShowOtp(false);
                                    setOtp(Array(OTP_LEN).fill(""));
                                    setIsLimitExceeded(false);
                                    setOtpCount(null);
                                    setLimitMessage("");
                                    setTimer(30);
                                    setResendCount(0);
                                }}
                                className="link-btn"
                            >
                                Change number
                            </button>
                        </div>
                    )}

                    {/* ── OTP — digit boxes ── */}
                    {showOtp && (
                        <div className="mt-3">
                            <div className="otp-boxes" onPaste={onOtpPaste}>
                                {otp.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={el => { otpRefs.current[idx] = el; }}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => onOtpInput(idx, e.target.value)}
                                        onKeyDown={e => onOtpKey(idx, e)}
                                        aria-label={`OTP digit ${idx + 1}`}
                                        className={digit ? "otp-box otp-box--filled" : "otp-box"}
                                    />
                                ))}
                            </div>

                            {/* Resend */}
                            <div className="text-end mt-2">
                                {isLimitExceeded ? (
                                    <span className="text-danger small font-weight-bold" style={{ fontSize: "12px", display: "block" }}>
                                        {limitMessage || "OTP limit exceeded. You can request maximum 3 OTPs within 1 hour."}
                                    </span>
                                ) : timer > 0 ? (
                                    <span className="small text-muted" style={{ fontSize: "13px" }}>
                                        Resend OTP in <strong style={{ color: "#F97316" }}>{formatTimer(timer)}</strong>
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={sendOtp}
                                        disabled={loading || timer > 0 || isLimitExceeded}
                                        className="link-btn"
                                        style={{
                                            background: "none",
                                            border: "none",
                                            color: "#F97316",
                                            fontWeight: 600,
                                            fontSize: "13px",
                                            cursor: "pointer",
                                            padding: 0
                                        }}
                                    >
                                        Resend OTP
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Action button ── */}
                    <div className="login_btn mt-4 rounded rounded-3">
                        {!showOtp ? (
                            <button
                                className="btn text-white w-100 login-btn"
                                onClick={sendOtp}
                                disabled={loading || mobile.length !== 10}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={18} color="inherit" thickness={5} />
                                        <span>Sending OTP...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Send OTP</span>
                                        <span className="login-btn__arrow">→</span>
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                className="btn text-white w-100 login-btn"
                                onClick={() => verifyOtp()}
                                disabled={loading || !otpComplete}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={18} color="inherit" thickness={5} />
                                        <span>Verifying...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Verify OTP</span>
                                        <span className="login-btn__arrow">→</span>
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    <div className="login-card__divider">
                        <span>OR</span>
                    </div>

                    <ul className="login-card__points">
                        <li>
                            <LockOutlinedIcon fontSize="small" />
                            Secure login with OTP
                        </li>
                        <li>
                            <VerifiedUserOutlinedIcon fontSize="small" />
                            Secure B2B Procurement Portal
                        </li>
                    </ul>
                </div>
            </section>

            <ToastContainer />
        </>
    );
};

export default Login;
